/**
 * Hook SWR centralisé avec support PWA et synchronisation cross-tabs
 *
 * Élimine la duplication du pattern hasInitialDataRef + useAppVisibility + event listeners
 * qui était répété dans tous les hooks API.
 *
 * Fonctionnalités :
 * - Revalidation automatique au retour sur l'application (PWA iOS/Android)
 * - Synchronisation cross-tabs via CustomEvents et localStorage
 * - Configuration SWR optimisée par défaut
 * - Support du filtrage d'événements personnalisé
 *
 * @module useSWRWithSync
 * @author Équipe Brydge
 * @since 2.1.0
 */

import {useEffect, useRef, useCallback} from 'react';
import useSWR from 'swr';
import {useAppVisibility} from './useAppVisibility';
import {getCustomEventName, getStorageKey} from '../utils/appEventSync';

/**
 * Configuration SWR par défaut optimisée pour PWA
 * Peut être surchargée par les options de chaque hook
 */
export const SWR_SYNC_CONFIG = {
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  dedupingInterval: 2000,
  focusThrottleInterval: 5000,
};

/**
 * Hook SWR avec support PWA et synchronisation cross-tabs intégrés
 *
 * Remplace le pattern répétitif :
 * - hasInitialDataRef + useEffect pour tracker le chargement initial
 * - useAppVisibility pour revalider au retour sur l'app
 * - useEffect avec event listeners pour la synchro cross-tabs
 *
 * @param {string|null} key - Clé SWR (URL ou null pour désactiver le fetch)
 * @param {Function} fetcher - Fonction de fetch
 * @param {Object} [options={}] - Options de configuration
 * @param {string|string[]} [options.eventTypes] - Type(s) d'événement à écouter (depuis APP_EVENTS)
 * @param {string} [options.companyId] - ID de l'entreprise pour le filtrage des événements
 * @param {Function} [options.shouldRevalidate] - Filtre personnalisé (event) => boolean
 * @param {boolean} [options.listenToStorage=true] - Écouter les événements localStorage (cross-tabs)
 * @param {Object} [options.swrOptions={}] - Options SWR additionnelles
 * @return {Object} Résultat SWR enrichi avec hasInitialData
 *
 * @example
 * // Cas simple - un seul type d'événement
 * const {data, mutate} = useSWRWithSync(url, fetcher, {
 *   eventTypes: APP_EVENTS.RESTAURANT.RESERVATIONS_UPDATED,
 *   companyId,
 * });
 *
 * @example
 * // Plusieurs types d'événements
 * const {data} = useSWRWithSync(url, fetcher, {
 *   eventTypes: [
 *     APP_EVENTS.RESTAURANT.TABLES_UPDATED,
 *     APP_EVENTS.RESTAURANT.AREAS_UPDATED,
 *   ],
 *   companyId,
 * });
 *
 * @example
 * // Avec filtre personnalisé
 * const {data} = useSWRWithSync(url, fetcher, {
 *   eventTypes: APP_EVENTS.RESTAURANT.RESERVATIONS_UPDATED,
 *   shouldRevalidate: (event) =>
 *     event.detail?.companyId === companyId &&
 *     event.detail?.reservationId === reservationId,
 * });
 *
 * @example
 * // Admin (sans companyId)
 * const {data} = useSWRWithSync(url, fetcher, {
 *   eventTypes: APP_EVENTS.ADMIN.COMPANIES_UPDATED,
 * });
 *
 * @example
 * // Avec options SWR personnalisées
 * const {data} = useSWRWithSync(url, fetcher, {
 *   eventTypes: APP_EVENTS.RESTAURANT.AVAILABILITY_UPDATED,
 *   companyId,
 *   swrOptions: {
 *     refreshInterval: 30000,
 *     errorRetryCount: 5,
 *   },
 * });
 */
export const useSWRWithSync = (key, fetcher, options = {}) => {
  const {
    eventTypes,
    companyId,
    shouldRevalidate,
    listenToStorage = true,
    swrOptions = {},
  } = options;

  const hasInitialDataRef = useRef(false);

  // Configuration SWR finale avec fusion des options
  const finalSwrOptions = {
    ...SWR_SYNC_CONFIG,
    ...swrOptions,
  };

  // Hook SWR avec configuration fusionnée
  const swrResult = useSWR(key, fetcher, finalSwrOptions);

  const {data, mutate} = swrResult;

  // Marquer les données initiales comme chargées
  useEffect(() => {
    if (data !== undefined && !hasInitialDataRef.current) {
      hasInitialDataRef.current = true;
    }
  }, [data]);

  // Callback de revalidation mémorisé
  const revalidate = useCallback(() => {
    if (hasInitialDataRef.current && key) {
      mutate();
    }
  }, [key, mutate]);

  // Revalidation au retour sur l'application (PWA support)
  // Respecte les options SWR : si revalidateOnFocus est désactivé, on désactive les listeners de visibilité
  const shouldRevalidateOnFocus = finalSwrOptions.revalidateOnFocus !== false;
  useAppVisibility(revalidate, {
    enabled: Boolean(key),
    debounceMs: 300,
    includeVisibility: shouldRevalidateOnFocus,
    includeFocus: shouldRevalidateOnFocus,
    includeOnline: finalSwrOptions.revalidateOnReconnect !== false,
  });

  // Écoute des événements pour synchronisation
  useEffect(() => {
    if (!eventTypes || typeof window === 'undefined') return;

    const events = Array.isArray(eventTypes) ? eventTypes : [eventTypes];

    /**
     * Determines if the event should trigger a revalidation.
     * Uses the custom filter if provided, otherwise checks the companyId.
     *
     * @param {CustomEvent} event - The event to evaluate.
     * @return {boolean} True if the event should trigger a revalidation, false otherwise.
     */
    const shouldTriggerRevalidation = (event) => {
      if (!hasInitialDataRef.current) return false;

      // Filtre personnalisé prioritaire
      if (shouldRevalidate) {
        return shouldRevalidate(event);
      }

      // Filtrage par companyId si spécifié
      if (companyId && event.detail?.companyId) {
        return event.detail.companyId === companyId;
      }

      // Pas de filtre = toujours revalider
      return true;
    };

    // Gestionnaire pour les CustomEvents (même onglet)
    const handleCustomEvent = (event) => {
      if (shouldTriggerRevalidation(event)) {
        mutate();
      }
    };

    // Gestionnaire pour les événements storage (autres onglets)
    const handleStorageChange = (event) => {
      if (!listenToStorage) return;

      // Vérifier si l'événement correspond à un de nos types
      const matchingEvent = events.find((eventType) => {
        const storageKey = getStorageKey(companyId, eventType);
        return event.key === storageKey;
      });

      if (matchingEvent && hasInitialDataRef.current) {
        mutate();
      }
    };

    // Enregistrer les listeners pour chaque type d'événement
    events.forEach((eventType) => {
      const customEventName = getCustomEventName(eventType);
      window.addEventListener(customEventName, handleCustomEvent);
    });

    // Enregistrer le listener storage si activé
    if (listenToStorage) {
      window.addEventListener('storage', handleStorageChange);
    }

    // Cleanup
    return () => {
      events.forEach((eventType) => {
        const customEventName = getCustomEventName(eventType);
        window.removeEventListener(customEventName, handleCustomEvent);
      });

      if (listenToStorage) {
        window.removeEventListener('storage', handleStorageChange);
      }
    };
  }, [eventTypes, companyId, shouldRevalidate, listenToStorage, mutate]);

  return {
    ...swrResult,
    hasInitialData: hasInitialDataRef.current,
  };
};

export default useSWRWithSync;
